import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import EsgCard from "../Esg/EsgCard";
import StartedWith from "../Esg/StartedWith";
import { useModal } from "../../contexts/ModalContext";

function FAQAccordion() {
    const [openIndex, setOpenIndex] = useState(0); // Default to first item open

    const faqs = [
        {
            question: "What is Life Cycle Assessment (LCA)?",
            answer: "Life Cycle Assessment (LCA) is a structured methodology used to evaluate the environmental impacts of a product, process, or service across its life cycle. This typically includes raw material extraction, manufacturing, distribution, use, and end-of-life stages, depending on the defined system boundary."
        },
        {
            question: "What are the key stages of an LCA study?",
            answer: "An LCA study typically consists of four main stages: Goal and Scope Definition (defining the purpose and boundaries), Life Cycle Inventory (LCI) - collecting data on inputs and outputs, Life Cycle Impact Assessment (LCIA) - evaluating environmental impacts, and Interpretation - analyzing results and drawing conclusions."
        },
        {
            question: "What is the difference between LCI and LCIA?",
            answer: "LCI (Life Cycle Inventory) involves collecting and quantifying all inputs (materials, energy) and outputs (emissions, waste) throughout a product's life cycle. LCIA (Life Cycle Impact Assessment) then evaluates the potential environmental impacts of these inputs and outputs, such as climate change, resource depletion, or toxicity."
        },
        {
            question: "What do \"functional unit\" and \"system boundary\" mean?",
            answer: "A functional unit is the quantified performance of a product system for use as a reference unit in an LCA study (e.g., '1 kg of product' or '1 km of transportation'). A system boundary defines which processes are included or excluded from the assessment, determining the scope of the analysis."
        },
        {
            question: "What system boundaries does the platform support?",
            answer: "myKarb supports various system boundaries including cradle-to-gate (raw materials to factory gate), cradle-to-grave (full life cycle including use and disposal), and cradle-to-cradle (including recycling and reuse). The platform allows you to define custom boundaries based on your specific assessment needs."
        },
        {
            question: "Does the platform use recognised LCA databases?",
            answer: "Yes, myKarb integrates with the ecoinvent database, one of the most widely recognized and comprehensive LCA databases globally. The platform provides access to over 26,000 emission datasets, helping you select the most appropriate data for your assessments."
        },
        {
            question: "How does the platform simplify LCA execution?",
            answer: "myKarb simplifies LCA execution through automation, AI-powered dataset selection, built-in calculation logic, and intuitive interfaces. It eliminates the need for complex spreadsheets, reduces manual errors, and enables teams to complete assessments in minutes rather than weeks, even without specialized LCA expertise."
        }
    ];

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <div className="space-y-4">
            {faqs.map((faq, index) => (
                <div key={index} className="pb-4 border-b border-gray-300">
                    <button
                        onClick={() => toggleAccordion(index)}
                        className="flex items-center justify-between w-full text-left"
                    >
                        <h3 className="text-base font-semibold text-gray-800 md:text-lg montserrat">
                            {faq.question}
                        </h3>
                        <span className="flex-shrink-0 ml-4">
                            <div className="flex items-center justify-center w-6 h-6 bg-orange-500 rounded-full">
                                {openIndex === index ? (
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 10L2 6L6 2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                ) : (
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 2L2 6L6 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </div>
                        </span>
                    </button>
                    {openIndex === index && (
                        <p className="mt-3 text-sm leading-relaxed text-gray-600 md:text-base montserrat">
                            {faq.answer}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

function WhoItsBuiltForAccordion() {
    const [openIndex, setOpenIndex] = useState(0); // Default to first item open

    const audiences = [
        {
            title: "Manufacturers",
            description: "Simplify LCA and PCF calculations for your products. myKarb helps manufacturers measure environmental impact accurately, identify hotspots, and make data-driven sustainability decisions without complex spreadsheets or lengthy processes."
        },
        {
            title: "Importers & Exporters",
            description: "Streamline CBAM compliance and reporting. myKarb enables seamless data collection from suppliers, automated CBAM declaration generation, and ensures you stay compliant with EU trade regulations efficiently."
        },
        {
            title: "Sustainability Consultants",
            description: "Deliver faster, more accurate assessments for your clients. myKarb empowers consultants to provide comprehensive LCA and PCF services with audit-ready outputs, enhancing your service delivery and client satisfaction."
        },
        {
            title: "Sustainability Teams",
            description: "Transform sustainability data into strategic insights. myKarb helps internal teams manage product carbon footprints, comply with regulations, and turn compliance into competitive advantages with AI-powered intelligence."
        }
    ];

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <div className="space-y-4">
            {audiences.map((audience, index) => (
                <div key={index} className="pb-4 border-b border-gray-400">
                    <button
                        onClick={() => toggleAccordion(index)}
                        className="flex items-center justify-between w-full text-left"
                    >
                        <h3 className="text-lg font-medium text-white md:text-xl montserrat">
                            {audience.title}
                        </h3>
                        <span className="flex-shrink-0 ml-4 text-2xl text-white">
                            {openIndex === index ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </span>
                    </button>
                    {openIndex === index && audience.description && (
                        <p className="mt-3 text-sm text-gray-200 md:text-base montserrat">
                            {audience.description}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

function MyKarb() {
    const navigate = useNavigate();
    const { openModal } = useModal();

    const handleBookDemo = () => {
        openModal();
    };

    const handleGetStarted = () => {
        window.location.href = 'https://mykarb.com/register-page';
    };

    const handleContactUs = () => {
        navigate('/contact-us');
    };

    const handleExplorePlans = () => {
        window.location.href = 'https://mykarb.com/#plans';
    };

    const howWeHelp = [
        {
            "title": "SIMPLIFIES COMPLEX REGULATIONS",
            "image": "/simplifieddata.png",
            "description": "Automatically translates regulatory requirements into clear, actionable workflows-so teams always know what to do and why it matters."
        },
        {
            "title": "CENTRALISES SUPPLY CHAIN DATA",
            "image": "/valuechain.png",
            "description": "Brings all environmental and operational data into one integrated platform, creating a single source of truth for sustainability reporting."
        },
        {
            "title": "AUTOMATES LCA & PCF REPORTING",
            "image": "/automated.png",
            "description": "Generates accurate, standardised carbon reports in minutes instead of weeks, dramatically reducing effort and turnaround time."
        },
        {
            "title": "ELIMINATES MANUAL ERRORS",
            "image": "/seemlesserp.png",
            "description": "Removes spreadsheet dependency through validated data flows, built-in checks, and consistent calculation logic, eliminating the need for expensive, specialised skillsets"
        },
        {
            "title": "ENHANCES INTERNAL CAPABILITY",
            "image": "/realtimedashboards.png",
            "description": "Supports teams with embedded intelligence, guidance, and regulatory context-reducing reliance on external experts."
        },
        {
            "title": "TURNS COMPLIANCE INTO STRATEGY",
            "image": "/unlimited users.png",
            "description": "Transforms data into insights that support cost optimisation, risk mitigation, and long-term sustainability planning."
        }
    ];

    const whyMyKarbFeatures = [
        {
            title: "SIMPLIFIES COMPLEX REGULATIONS",
            description: "Automatically interprets evolving compliance requirements into clear, easy-to-follow actions.",
            image: "/simplifieddata.png",
            alt: "Simplifies Complex Regulations"
        },
        {
            title: "CENTRALIZES SUPPLY CHAIN DATA",
            description: "Brings all your sustainability data into one reliable, integrated platform.",
            image: "/valuechain.png",
            alt: "Centralizes Supply Chain Data"
        },
        {
            title: "AUTOMATES REPORTING",
            description: "Generates accurate reports in minutes – saving time and effort.",
            image: "/automated.png",
            alt: "Automates Reporting"
        },
        {
            title: "ELIMINATES MANUAL ERRORS",
            description: "Replaces spreadsheets with automated, validated data flows.",
            image: "/seemlesserp.png",
            alt: "Eliminates Manual Errors"
        },
        {
            title: "ENHANCES INTERNAL EXPERTISE",
            description: "Guides teams with built-in intelligence and regulatory insights.",
            image: "/realtimedashboards.png",
            alt: "Enhances Internal Expertise"
        },
        {
            title: "TRANSFORMS COMPLIANCE INTO STRATEGY",
            description: "Turns data into decisions that drive measurable sustainability impact.",
            image: "/unlimited users.png",
            alt: "Transforms Compliance Into Strategy"
        }
    ];

    const aiPoweredFeatures = [
        {
            title: "Automation at Scale",
            description: "LCA – PCF calculations and CBAM reporting reduced from weeks to minutes, eliminating manual errors and inefficiencies.",
            icon: (
                <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            )
        },
        {
            title: "Intelligence for Clarity",
            description: "Validate data, identify environmental hotspots, and interpret complex regulations with AI-powered precision, ensuring audit-ready reports.",
            icon: (
                <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
            )
        },
        {
            title: "Predictive Insights",
            description: "Leverage ML models to anticipate compliance risks, forecast environmental impacts, and develop proactive sustainability strategies.",
            icon: (
                <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            )
        }
    ];

    const modules = [
        {
            title: "Life Cycle Assessment (LCA) – Product Carbon Footprint (PCF) Module",
            features: [
                "Measure the environmental footprint of products in minutes.",
                "Identify hotspots across raw materials, production, and operations.",
                "Support cost efficiency and strategic sustainability decisions.",
                "Fast-track environmental assessments with clear, accurate dashboards."
            ],
            image: "/mykarb3.png",
            imagePosition: "left" // left or right
        },
        // {
        //     title: "Carbon Border Adjustment Mechanism (CBAM) Module",
        //     features: [
        //         "Seamlessly comply with EU trade regulations without needing experts.",
        //         "Intuitive interface to import data directly from suppliers.",
        //         "Download complete CBAM declarations ready for submission.",
        //         "Reduce reporting errors, save time, and stay aligned with evolving policies."
        //     ],
        //     image: "/mykarb4-new.png",
        //     imagePosition: "left"
        // }
    ];

    return (
        <div className="flex flex-col justify-center text-white bg-white">
            <Helmet>
                <title>LCA PCF Software for Carbon Footprint Reporting | Nirantara Solutions</title>
                <meta name="description" content="myKarb by Nirantara is an LCA PCF software platform for carbon footprint reporting, combining AI and 26,000+ verified emission datasets to help organisations calculate product carbon footprints, track Scope 1, 2 & 3 emissions, and generate audit-ready sustainability reports." />
                <meta name="keywords" content="LCA PCF Software for Carbon Footprint Reporting, product carbon footprint calculation services, carbon footprint consultant, Scope 1 2 3 emissions tracking software, GHG inventory and verification services, sustainability consulting for carbon reduction, automated ESG reporting services, LCA PCF software" />
                <link rel="canonical" href="https://nirantara.solutions/myKarb" />
            </Helmet>
            <div className="flex flex-col justify-center text-white bg-white">
                <div className="flex flex-col justify-center w-full bg-white max-md:max-w-full">
                    <section className="flex overflow-hidden relative flex-col justify-end items-start px-8 md:px-16 lg:px-24 pb-16 md:pb-20 pt-32 w-full min-h-[calc(100vh)] max-md:px-5 max-md:pb-12 max-md:max-w-full">
                        <img
                            loading="lazy"
                            src="/mykarb.jpg"
                            alt="myKarb Hero Background"
                            className="absolute inset-0 object-cover size-full"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                        <div className="flex z-10 relative flex-col items-start w-full max-w-[600px] max-md:max-w-full">
                            <h2 className="text-4xl font-black leading-tight text-left text-white md:text-5xl lg:text-6xl max-md:text-3xl helvetica">
                                myKarb
                            </h2>
                            <button
                                onClick={handleGetStarted}
                                className="flex items-center gap-3 px-8 py-4 mt-6 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                style={{ backgroundColor: '#2C5F7C' }}
                            >
                                <span className="montserrat">Get Started</span>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </section>

                    {/* Sustainability Compliance Section */}
                    <section className="w-full py-16 bg-gray-50 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h1 className="mb-8 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Sustainability Compliance Made Simple, Fast, And Reliable
                            </h1>
                            <div className="space-y-6 text-gray-700">
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                    With rising sustainability compliance and mandates, organisations need to set targets for their sustainability performance. To enable this, organisations need to measure their products' environmental impact. Life Cycle Assessment (LCA)/ Product Carbon Footprint (PCF) helps organisations achieve this goal, making them the gold standard in sustainability assessment.
                                </p>
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                    myKarb is Nirantara's intelligent compliance platform designed to help organisations measure, manage, and report the environmental impact of products with confidence. From Life Cycle Assessment (LCA)/ Product Carbon Footprint (PCF) reporting, myKarb replaces weeks of manual effort with structured automation, turning compliance into a business advantage, within minutes, rather than an operational burden.
                                </p>
                            </div>
                            <div className="mt-12">
                                <h3 className="mb-6 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px' }}>
                                    What is myKarb?
                                </h3>
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px', color: '#374151' }}>
                                    myKarb is a purpose-built Lifecycle assessment platform that transforms raw data into clear, decision-ready insights, using ecoinvent database and AI. Built for manufacturers, consultants, and sustainability teams, myKarb enables faster assessments, accurate reporting, and audit-ready outputs without spreadsheets or guesswork. myKarb helps organisations stay compliant, minimise risk, and use sustainability data more practically and effectively.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* The Challenge Businesses Face Today Section */}
                    <section className="w-full py-16 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%', backgroundColor: '#2C5F7C' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-12 text-center text-white md:mb-16" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                The Challenge Businesses Face Today
                            </h2>

                            <div className="grid items-center grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
                                <div className="text-white">
                                    <p className="mb-8" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        Most organisations struggle with sustainability compliance due to many reasons.
                                    </p>
                                    <div className="space-y-6">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                                Manual LCA / PCF calculations take weeks to complete in spreadsheets, and do not guarantee error-free results.
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                                Selecting the correct emission dataset from amongst 26000+ datasets in ecoinvent requires skills and expertise. In the absence of an easy-to-use and accurate tool for their LCA/ PCF, companies face increased regulatory risk, delayed reporting cycles, and limit the value organisations can extract from their sustainability data.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full h-full">
                                    <img
                                        src="/vajra-5.jpg"
                                        alt="Sustainability challenge"
                                        className="object-cover w-full h-full rounded-lg shadow-2xl"
                                        style={{ border: '2px solid #87CEEB' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* How myKarb Solves It Section */}
                    <div className="flex flex-col items-center w-full gap-10 sm:gap-14 px-[2rem] py-[2rem] sm:px-[5rem] md:px-[5rem] lg:px-[15rem] montserrat" style={{ backgroundColor: '#ECECEC' }}>
                        <h2 className="font-bold text-center text-black sm:pt-3" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px' }}>
                            How myKarb Solves It
                        </h2>
                        <p className="text-center text-black max-w-4xl mb-4" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                            myKarb replaces fragmentation and manual effort with a single, structured system built for scale and accuracy.
                        </p>
                        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16 w-full">
                            {howWeHelp && howWeHelp.length > 0 && howWeHelp.map((item, index) => {
                                const { title, subtitle, description, cta, image } = item;
                                return (
                                    <EsgCard minHeight="250px" title={title} subtitle={subtitle} description={description} cta={cta} Icon={image} key={index} />
                                )
                            })}
                        </div>
                        <div className="mt-8">
                            <button
                                onClick={handleGetStarted}
                                className="flex items-center gap-2 px-8 py-4 text-base font-semibold text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                style={{ backgroundColor: '#1C486B' }}
                            >
                                <span className="montserrat">Start Your ESG Journey</span>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* About MyKarb Section */}
                    {/* <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12 items-center">
                                <div>
                                    <h2 className="mb-6 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                        About MyKarb
                                    </h2>
                                    
                                    <div className="space-y-4 mb-6 text-gray-700">
                                        <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                            At Nirantara, we built myKarb to simplify sustainability compliance for businesses of all sizes. Powered by automation and AI, myKarb helps organizations measure, manage, and report their environmental footprint with speed and precision.
                                        </p>
                                        <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                            From Life Cycle Assessments (LCA)- Product Carbon Footprint (PCF) to Carbon Border Adjustment Mechanism (CBAM) compliance, myKarb turns complex regulations into simple, actionable insights, helping businesses save time, reduce risk, and make smarter sustainability decisions.
                                        </p>
                                    </div>

                                    <div className="space-y-4 mb-6">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <p className="text-base font-medium text-gray-800 montserrat md:text-lg">
                                                Faster LCA - PCF Processing
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <p className="text-base font-medium text-gray-800 montserrat md:text-lg">
                                                Seamless CBAM Reporting
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                </svg>
                                            </div>
                                            <p className="text-base font-medium text-gray-800 montserrat md:text-lg">
                                                AI-Powered Insights
                                            </p>
                                        </div>
                                    </div>

                                    <p className="mb-8 text-gray-700" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        Whether you're a manufacturer, importer, or consultant, myKarb delivers clarity, speed, and confidence across your sustainability journey.
                                    </p>

                                    <div className="flex flex-col gap-4 sm:flex-row">
                                        <button
                                            onClick={handleContactUs}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-blue-600 transition-all duration-300 border-2 border-blue-600 rounded-lg md:text-lg hover:bg-blue-50 montserrat"
                                        >
                                            Contact Us
                                        </button>
                                        <button
                                            onClick={handleGetStarted}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90 montserrat"
                                            style={{ backgroundColor: '#2C5F7C' }}
                                        >
                                            Get Started
                                        </button>
                                    </div>
                                </div>

                                <div className="w-full h-full">
                                    <img
                                        src="/mykarb2new.jpg"
                                        alt="MyKarb Sustainability Platform"
                                        className="object-cover w-full h-full rounded-lg shadow-2xl"
                                    />
                                </div>
                            </div>
                        </div>
                    </section> */}

                    {/* Features of myKarb Section */}
                    {/* <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-4 text-center text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                            Why myKarb? 
                            </h2>

                            <div className="grid grid-cols-1 gap-6 mb-12 md:grid-cols-2 lg:grid-cols-3 md:gap-8">
                                {whyMyKarbFeatures.map((feature, index) => (
                                    <div key={index} className="flex flex-col items-center p-6 text-center rounded-lg md:p-8">
                                        <div className="flex items-center justify-center w-24 h-24 p-4 mb-6 overflow-hidden rounded-2xl">
                                            <img
                                                src={feature.image}
                                                alt={feature.alt}
                                                className="object-contain w-full h-full"
                                            />
                                        </div>
                                        <h3 className="mb-3 text-xl font-semibold text-gray-800 montserrat">{feature.title}</h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                            {feature.description}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                                <button
                                    onClick={handleContactUs}
                                    className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-blue-600 transition-all duration-300 border-2 border-blue-600 rounded-lg md:text-lg hover:bg-blue-50 montserrat"
                                >
                                    Contact Us
                                </button>
                                <button
                                    onClick={handleGetStarted}
                                    className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90 montserrat"
                                    style={{ backgroundColor: '#2C5F7C' }}
                                >
                                    Get Started
                                </button>
                            </div>
                        </div>
                    </section> */}

                    {/* Smarter Compliance Powered by AI Section */}
                    {/* <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-16 text-center text-black md:mb-20" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Smarter Compliance Powered by AI
                            </h2>

                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 md:gap-10">
                                {aiPoweredFeatures.map((feature, index) => (
                                    <div 
                                        key={index} 
                                        className={`flex flex-col items-center p-6 text-center ${
                                            index === 0 ? 'border-b border-r border-gray-200' : 
                                            index === 1 ? 'border-b border-r border-gray-200' : 
                                            'border-b border-gray-200'
                                        }`}
                                    >
                                        <div className="mb-6">
                                            {feature.icon}
                                        </div>
                                        <h3 className="mb-3 text-xl font-semibold text-black montserrat">{feature.title}</h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                            {feature.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section> */}

                    {/* Our Modules Section */}
                    <section className="w-full py-16 bg-gray-50 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-12 text-center text-black md:mb-16" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Our Modules
                            </h2>

                            {modules.map((module, index) => (
                                <div
                                    key={index}
                                    className={`grid items-center grid-cols-1 gap-8 mb-16 lg:grid-cols-2 lg:gap-12 md:mb-24 ${index === modules.length - 1 ? 'mb-12' : ''
                                        }`}
                                >
                                    {/* Image */}
                                    <div className={`relative ${module.imagePosition === 'left' ? 'order-2 lg:order-1' : 'order-2 lg:order-2'}`}>
                                        <div className="absolute bottom-0 w-3/4 transform -translate-x-1/2 rounded-full left-1/2 h-3/4" style={{ backgroundColor: '#EB7E0E66', backdropFilter: 'blur(100px)', filter: 'blur(100px)', zIndex: 0 }}></div>
                                        <img
                                            src={module.image}
                                            alt={module.title}
                                            className="relative z-10 w-full rounded-lg shadow-2xl"
                                        />
                                    </div>

                                    {/* Content */}
                                    <div className={module.imagePosition === 'left' ? 'order-1 lg:order-2' : 'order-1 lg:order-1'}>
                                        <h3 className="mb-6 text-2xl font-semibold text-black md:text-3xl lg:text-4xl md:mb-8" style={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                                            {module.title}
                                        </h3>
                                        <ul className="space-y-3 text-base leading-relaxed text-gray-600 md:text-lg montserrat">
                                            {module.features.map((feature, featureIndex) => (
                                                <li key={featureIndex} className="flex items-start">
                                                    <span className="flex-shrink-0 w-2 h-2 mt-2 mr-3 rounded-full bg-orange-500"></span>
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ))}

                            {/* Get Started Button */}
                            {/* <div className="flex justify-center mt-12 md:mt-16">
                                <button
                                    onClick={handleGetStarted}
                                    className="flex items-center gap-3 px-8 py-4 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                    style={{ backgroundColor: '#2C5F7C' }}
                                >
                                    <span className="montserrat">Get Started</span>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div> */}
                        </div>
                    </section>

                    {/* StartedWith Section */}
                    <StartedWith
                        heading={"Flexible Subscriptions for Every Business"}
                        content1={"Whether you are a growing manufacturer or a sustainability consultant managing multiple clients, myKarb offers flexible plans tailored to your operational scale and reporting needs."}
                        content2={"Drive your carbon journey with confidence, using Nirantara's intelligent compliance platform, built to grow with your business."}
                        button1={"Explore Plans"}
                        button2={"Get Started Today"}
                        btn1OnClick={handleExplorePlans}
                        btn2OnClick={handleGetStarted}
                    />

                    {/* Frequently Asked Questions Section */}
                    <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-12 text-center text-black md:mb-16" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Frequently Asked Questions
                            </h2>
                            <FAQAccordion />
                        </div>
                    </section>

                    {/* Struggling to Manage LCA and PCF Reporting at Scale Section */}
                    <section className="w-full py-8 bg-white md:py-12" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
                                <div>
                                    <h2 className="mb-6 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                        Struggling to Manage LCA and PCF Reporting at Scale?
                                    </h2>
                                    <p className="mb-8 text-gray-600" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        myKarb provides a structured platform to calculate, manage, and report product-level environmental impact with accuracy, traceability, and audit readiness, without spreadsheet dependency.
                                    </p>
                                    <div className="flex flex-col gap-4 sm:flex-row">
                                        <button
                                            onClick={handleContactUs}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium transition-all duration-300 border-2 rounded-lg md:text-lg hover:bg-blue-50 montserrat"
                                            style={{ color: '#1C486B', borderColor: '#1C486B', backgroundColor: 'white' }}
                                        >
                                            Contact Us
                                        </button>
                                        <button
                                            onClick={handleGetStarted}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90 montserrat"
                                            style={{ backgroundColor: '#1C486B' }}
                                        >
                                            Get Started Today
                                        </button>
                                    </div>
                                </div>
                                <div className="flex justify-center lg:justify-end">
                                    <img
                                        src="/vajra-6.png"
                                        alt="LCA and PCF Reporting Platform"
                                        className="object-contain w-full h-auto max-w-md"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Struggling to Measure and Manage ESG Performance at Scale Section */}
                    <section className="w-full py-8 bg-white md:py-12" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
                                <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
                                    <img
                                        src="/esg-performance.png"
                                        alt="ESG Performance Management Dashboard"
                                        className="object-contain w-full h-auto max-w-md"
                                    />
                                </div>
                                <div className="order-1 lg:order-2">
                                    <h2 className="mb-6 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                        Struggling to Measure and Manage ESG Performance at Scale?
                                    </h2>
                                    <p className="mb-8 text-gray-600" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        Vajra simplifies ESG management by consolidating environmental, social, and governance data, providing teams with a clear, real-time view across operations and business units, making it easy for companies to track data, generate reports, and meet compliance, cost-effectively.
                                    </p>
                                    <div className="flex flex-col gap-4 sm:flex-row">
                                        <button
                                            onClick={handleBookDemo}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90 montserrat"
                                            style={{ backgroundColor: '#1C486B' }}
                                        >
                                            Book a Demo
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default MyKarb;